import EventAvailableIcon from "@mui/icons-material/EventAvailableRounded";
import HistoryIcon from "@mui/icons-material/HistoryRounded";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformationRounded";
import MoreHorizIcon from "@mui/icons-material/MoreHorizRounded";
import PsychologyIcon from "@mui/icons-material/PsychologyRounded";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import ScienceIcon from "@mui/icons-material/ScienceRounded";
import { motion } from "framer-motion";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getTimelineEvents } from "../../services/timeline.service";
import "./Timeline.scss";

const categories = {
  "Lab Results": { icon: ScienceIcon, color: "blue" },
  Vaccination: { icon: MedicalInformationIcon, color: "green" },
  Medication: { icon: MedicalInformationIcon, color: "purple" },
  Appointment: { icon: EventAvailableIcon, color: "amber" },
  "AI Insight": { icon: PsychologyIcon, color: "indigo" },
  Other: { icon: MoreHorizIcon, color: "slate" }
};

export default function Timeline() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getTimelineEvents();
      setEvents(Array.isArray(data) ? data : []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.08 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -16 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.45, ease: "easeOut" }
    }
  };

  const reportCount = events.filter((e) => String(e._id || "").startsWith("report:")).length;

  return (
    <div className="timeline-container">
      <div className="timeline-header">
        <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="timeline-header__title-row">
            <HistoryIcon className="timeline-header__icon" />
            <h1>Health Journey</h1>
          </div>
          <p>Chronological overview of your medical milestones, uploaded lab reports, and AI-generated insights.</p>
          <div className="timeline-header__actions">
            <Link to="/upload" className="timeline-header__link">
              Upload report
            </Link>
            <button type="button" className="timeline-header__refresh" onClick={fetchEvents} disabled={loading}>
              <RefreshIcon sx={{ fontSize: 20 }} />
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
          {!loading && events.length > 0 && (
            <p className="timeline-header__meta">
              {events.length} milestone{events.length !== 1 ? "s" : ""}
              {reportCount > 0 ? ` · ${reportCount} from your PDF uploads` : ""}
            </p>
          )}
        </motion.div>
      </div>

      <div data-tour="timeline-view" className="timeline-content">
        {loading ? (
          <div className="timeline-loading">
            <div className="timeline-loading__spinner" />
            <span>Loading your history…</span>
          </div>
        ) : events.length === 0 ? (
          <motion.div className="timeline-empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <HistoryIcon />
            <h3>No milestones yet</h3>
            <p>Upload a medical report PDF — it will appear here automatically with the date we processed it.</p>
            <Link to="/upload" className="timeline-empty__btn">
              Upload a report
            </Link>
          </motion.div>
        ) : (
          <motion.div className="timeline" variants={containerVariants} initial="hidden" animate="visible">
            <div className="timeline__track" />

            {events.map((event, index) => {
              const category = categories[event.type] || categories.Other;
              const Icon = category.icon;
              const ts = event.timestamp;
              const date = ts ? new Date(ts) : null;
              const dateValid = date && !Number.isNaN(date.getTime());

              return (
                <motion.div key={event._id || index} className="timeline-item" variants={itemVariants}>
                  <div className={`timeline-item__node timeline-item__node--${category.color}`}>
                    <Icon />
                  </div>

                  <div className="timeline-item__content">
                    <div className="timeline-item__header">
                      <span className={`timeline-item__type timeline-item__type--${category.color}`}>{event.type}</span>
                      <span className="timeline-item__date">
                        {dateValid
                          ? date.toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                              year: "numeric"
                            })
                          : "—"}
                      </span>
                    </div>

                    <h3 className="timeline-item__title">{event.title || "Health event"}</h3>
                    <p className="timeline-item__description">
                      {event.description || "Milestone recorded in your health journey."}
                    </p>

                    {event.metrics && Object.keys(event.metrics).length > 0 && (
                      <div className="timeline-item__metrics">
                        {Object.entries(event.metrics).map(([key, value]) => (
                          <div key={key} className="timeline-metric">
                            <span className="timeline-metric__label">{key}</span>
                            <span className="timeline-metric__value">{String(value)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
}
